import React, { useState, useRef, useEffect } from 'react';
import { Globe, X } from 'lucide-react';
import { triggerPiepeline, PipelineRun, cancelPipeline, fetchWebsiteDocumentCache } from '../../services/api/virtualAssistant';
import { WebsiteData, ExcludedItems } from "../../types/CourseData";
import CustomModal from "../../components/CustomModal";
import WarningModal from "../../components/WarningModal";
import { v4 as uuidv4 } from 'uuid';
import WebsiteSidebar from './WebsiteSidebar';
import WebsiteConnectTab from './WebsiteConnectTab';
import WebsitePreviewTab from './WebsitePreviewTab';
import WebsiteFooter from './WebsiteFooter';

interface Pipeline {
  id: string;
  run_type: string;
  start_ts: string;
  status: string;
  end_ts: string;  
  docs_processed_count: number;
  docs_failed_count: number;
  message: string;
  failedDocuments: [];
}

interface PublicWebsitesProps {
  isWebsiteModalOpen: boolean;
  setWebsiteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  websites: WebsiteData[];
  setWebsites: React.Dispatch<React.SetStateAction<WebsiteData[]>>;
  updateExcludedItems: (courseId: number, type: keyof ExcludedItems, itemId: string) => void;
  assistant: any;
  triggerAutoSave: () => void; 
  isWebsiteRNightly: boolean;
  setIsWebsiteRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  isWebsiteFullLoad: boolean;
  setIsWebsiteFullLoad: React.Dispatch<React.SetStateAction<boolean>>;
}

const PublicWebsites: React.FC<PublicWebsitesProps> = ({
  isWebsiteModalOpen,
  setWebsiteModalOpen,
  websites,
  setWebsites,
  updateExcludedItems,
  assistant,
  triggerAutoSave,
  isWebsiteRNightly,
  setIsWebsiteRNightly,
  isWebsiteFullLoad,
  setIsWebsiteFullLoad
}) => {
  const [selectedWebsite, setSelectedWebsite] = useState<number>(1);
  const [pipelineData, setPipelineData] = useState<Pipeline[]>([]);
  const [pendingSave, setPendingSave] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalRefreshNModal, setModalRefreshNModal] = useState(false);
  const [lastSaveTarget, setLastSaveTarget] = useState<'azure' | 'azure'>('azure');
  const [isGettingDataCalled, setIsGetDataCallled] = useState(true);
  const [gettingData, setGettingData] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<null | {
    progress: number;
    status: string;
    error?: string;
  }>(null);
  const [getDataClickTime, setGetDataClickTime] = useState<Date | null>(null);
  const [systemConfig, setSystemConfig] = useState<{ Id: string; ConfigValue: string; }[]>([]);
  const [cancellingPipeline, setCancellingPipeline] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCheckedInitial = useRef(false);
  const gettingDataRef = useRef(false);

  const [customModal, setCustomModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
  });

  // Cache utilities
  const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000;
  const getCachedData = (key: string) => {
    const item = sessionStorage.getItem(key);
    if (!item) return null;
    const { value, timestamp } = JSON.parse(item);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  };

  // Initialize system config
  useEffect(() => {
    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  // Reset initial-check guard when modal closes
  useEffect(() => {
    if (!isWebsiteModalOpen) {
      hasCheckedInitial.current = false;
    }
  }, [isWebsiteModalOpen]);

  // Initialize websites if empty
  useEffect(() => {
    if (websites.length === 0) {
      addNewWebsites();
    }
  }, [websites.length]);

  // Pipeline data fetching
  const fetchPipelineData = async () => {
    const pipeline_name = "website_to_vectordb";
    if (assistant.app_id) {
      const resPipelineRun = await PipelineRun(pipeline_name, assistant.app_id);

      // ALWAYS store ALL pipelines for history display
      setPipelineData(resPipelineRun);

      // Filter: Only consider pipelines created AFTER user clicked "Get Data Now" for STATUS determination
      let relevantPipeline = resPipelineRun;
      if (getDataClickTime && resPipelineRun.length > 0) {
        relevantPipeline = resPipelineRun.filter((p: any) => {
          const pipelineCreatedAt = new Date(p.created_ts);
          return pipelineCreatedAt >= getDataClickTime;
        });

        // If no new pipelines found yet, keep polling but don't show old status
        if (relevantPipeline.length === 0 && gettingDataRef.current) {
          setCurrentStatus({ progress: 20, status: "In Progress" });
          setIsGetDataCallled(true);
          return;  // Early return - pipelineData still has all history
        }
      }

      // Use relevantPipeline ONLY for status determination
      if (relevantPipeline && relevantPipeline.length > 0) {
        setIsGetDataCallled(true); // Pipeline data exists

        const backendStatus = relevantPipeline[0].status;
        let status = '';

        // Map backend status to UI status - ALWAYS check actual status first
        if (backendStatus === 'STARTED') status = 'In Progress';
        if (backendStatus === 'failed') {
          status = 'Failed';
          gettingDataRef.current = false;
          setGettingData(false);
        }
        if (backendStatus === 'success') {
          status = 'Complete';
          gettingDataRef.current = false;
          setGettingData(false);
        }
        if (backendStatus === 'cancelled') {
          status = 'Cancelled';
          setCancellingPipeline(false);
          gettingDataRef.current = false;
          setGettingData(false);
        }
        if (backendStatus === 'cancelling') {
          status = 'Cancelling';
          setCancellingPipeline(true);
        }
        if (backendStatus === 'cancel_failed') {
          status = 'Failed';
          setCancellingPipeline(false);
          gettingDataRef.current = false;
          setGettingData(false);
        }

        setCurrentStatus({ progress: 20, status: status });
      } else {
        if (gettingDataRef.current) {
          setCurrentStatus({ progress: 20, status: "In Progress" });
          setIsGetDataCallled(true);
        } else {
          setCurrentStatus({ progress: 0, status: "" });
          setIsGetDataCallled(false);
        }
      }
    }
  };

  // Pipeline polling effect
  useEffect(() => {
    fetchPipelineData();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Update condition to include cancel states
    if (currentStatus?.status !== 'Complete' && currentStatus?.status !== 'Cancelled') {
      if (isGettingDataCalled) {
        intervalRef.current = setInterval(() => {
          fetchPipelineData();
        }, 10 * 1000);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentStatus?.status, isGettingDataCalled, assistant.app_id]);

  // Auto-polling for connected websites without data
  const fetchCalledRef = useRef<{ [key: number]: boolean }>({});
  const pollingTimeouts = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const clearPolling = (websiteId: number) => {
    const timeoutId = pollingTimeouts.current.get(websiteId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pollingTimeouts.current.delete(websiteId);
    }
  };

  const pollWebScrape = async (websiteId: number, source: string, document_id?: string) => {
    const pollInterval = 20000;
    const website = websites.find(c => c.id === websiteId);
    const documentId = document_id ? document_id : website?.document_id;

    const fetchStatus = async () => {
      try {
        if (documentId) {
          const result = await fetchWebsiteDocumentCache(documentId);

          if (result.status === 'failed') {
            setWebsites(prev =>
              prev.map(c =>
                c.id === websiteId
                  ? {
                      ...c,
                      connectionState: 'connected',
                      status: 'failed',
                    }
                  : c
              )
            );
            setPendingSave(false);
            clearPolling(websiteId);
          }

          if (result.status === 'completed') {
            setWebsites(prev => prev.map(course =>
              course.id === websiteId ? { ...course, activeTab: 'PREVIEW' } : course
            ));

            if (result.data) {
              const parsedData = result.data;
              setWebsites(prev =>
                prev.map(c =>
                  c.id === websiteId
                    ? {
                        ...c,
                        connectionState: 'connected',
                        websites_links: parsedData || [],
                        status: 'connected',
                      }
                    : c
                )
              );
              triggerAutoSave();
              // Only set pendingSave when new tree data arrives AND either:
              // 1. No pipeline data exists (never saved before), OR
              // 2. User manually triggered this (source is 'click')
              if (!gettingDataRef.current && source === 'click') {
                setPendingSave(true);
              }
              clearPolling(websiteId);
            }
          }

          if (result.status === 'pending' || result.status === 'running') {
            setWebsites(prev => prev.map(course =>
              course.id === websiteId ? { ...course, activeTab: 'PREVIEW' } : course
            ));

            setWebsites(prev => prev.map(c =>
              c.id === websiteId
                ? {
                    ...c,
                    connectionState: 'connected',
                    status: 'connecting',
                    websites_links: [],
                  }
                : c
            ));

            const timeoutId = setTimeout(fetchStatus, pollInterval);
            pollingTimeouts.current.set(websiteId, timeoutId);
          }

          if (result?.detail) {
            setWebsites(prev =>
              prev.map(c =>
                c.id === websiteId
                  ? {
                      ...c,
                      status: 'failed',
                      error: result.detail,
                    }
                  : c
              )
            );
            clearPolling(websiteId);
          }
        }
      } catch (error) {
        setWebsites(prev =>
          prev.map(c =>
            c.id === websiteId
              ? {
                  ...c,
                  status: 'failed',
                }
              : c
          )
        );
        setPendingSave(false);
        clearPolling(websiteId);
      }
    };

    clearPolling(websiteId);
    fetchStatus();
  };

  // Auto-start polling for connected websites without data
  useEffect(() => {
    websites.forEach((website) => {
      if (website.connectionState === 'connected' &&
          !fetchCalledRef.current[website.id] &&
          !website.websites_links?.length &&
          website.document_id) {
        fetchCalledRef.current[website.id] = true;
        pollWebScrape(website.id, 'auto', website.document_id);
      }
    });
  }, [websites]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      pollingTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
      pollingTimeouts.current.clear();
    };
  }, []);

  // Cancel pipeline function
  const handleCancelPipeline = async () => {
    try {
      setCancellingPipeline(true);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'Cancelling' }));

      await cancelPipeline("website_to_vectordb", assistant.app_id);

      // Poll for status update after cancel request
      setTimeout(() => {
        fetchPipelineData();
      }, 2000);
    } catch (error) {
      console.error('Failed to cancel pipeline:', error);
      setCancellingPipeline(false);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'In Progress' }));
      
      setCustomModal({
        isOpen: true,
        title: "Cancel Failed",
        message: "Failed to cancel the operation. Please try again.",
        type: "error",
      });
    }
  };

  const addNewWebsites = () => {
    const newId = websites.length > 0 ? Math.max(...websites.map(w => w.id)) + 1 : 1;
    let crawl_depth = systemConfig.find(item => item.Id === 'crawl_depth')?.ConfigValue ?? '2';

    setWebsites(prev => [
      ...prev,
      {
        id: newId,
        website_id: '',
        url: '',
        order: newId,
        website_name: `Website ${newId}`,
        activeTab: 'CONNECT',
        connectionState: 'idle',
        status: 'idle',
        document_id: '',
        includeExternalLinks: false,
        restrictToStartPath: true,
        excludePatterns: [],
        followLinkLevel: String(crawl_depth),
        websites_links: [],
        excludedLinks: [],
      },
    ]);
    setSelectedWebsite(newId);
  };

  const handleClose = () => {
    const hasConnectedWebsite = websites?.some(w => w.connectionState === "connected");
    const hasUnsavedChanges = !pipelineData || pipelineData.length === 0 || pendingSave;

    if (hasConnectedWebsite && hasUnsavedChanges) {
      setCustomModal({
        isOpen: true,
        title: "Save Confirmation",
        message: "Your changes have not been saved. Are you sure you want to close?",
        type: "confirmation",
        onConfirm: () => {
          setCustomModal(prev => ({ ...prev, isOpen: false }));
          setWebsiteModalOpen(false);
        }
      });
    } else {
      setWebsiteModalOpen(false);
    }
  };

  const selectedCourseData = websites.find(c => c.id === selectedWebsite);

  return (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-60 ${
        isWebsiteModalOpen ? 'block' : 'hidden'
      }`}
      onClick={() => setWebsiteModalOpen(false)}
    >
      <div
        className="bg-white w-[90%] h-[80%] shadow-lg rounded-lg flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="pl-3 pr-3 pb-3 pt-2 sticky top-0 z-10 shadow-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Globe className="h-6 w-6 text-gray-700" />
              <h2 className="text-xl font-semibold text-gray-800">Public Websites</h2>
            </div>
            <div className="flex items-center space-x-6">
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <p className="text-gray-600 mt-2">
            Setup your Chatbot to train itself with content on websites. Content should be publicly accessible and should not require
            authentication. You can specify up to {(Number(systemConfig.find(item => item.Id === 'va_max_allowed_websites')?.ConfigValue) || 5)} website URLs.
          </p>
        </div>

        <div className="overflow-auto mt-[2%] h-full">
          <div className="flex flex-1 max-h-[85%] min-h-[85%]">
            {/* Sidebar */}
            <WebsiteSidebar
              websites={websites}
              setWebsites={setWebsites}
              selectedWebsite={selectedWebsite}
              setSelectedWebsite={setSelectedWebsite}
              systemConfig={systemConfig}
              addNewWebsites={addNewWebsites}
              triggerAutoSave={triggerAutoSave}
              setCustomModal={setCustomModal}
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-gray-200">
                <div className="flex px-6 justify-between items-center">
                  <div className="flex">
                    <button
                      className={`px-4 py-2 border-b-2 font-medium ${
                        selectedCourseData?.activeTab === 'CONNECT' 
                          ? 'border-black text-black' 
                          : 'border-transparent text-gray-500'
                      }`}
                      onClick={() => selectedWebsite && setWebsites(prev => prev.map(course => 
                        course.id === selectedWebsite ? { ...course, activeTab: 'CONNECT' } : course
                      ))}
                    >
                      CONNECT
                    </button>
                    <button
                      className={`px-4 py-2 border-b-2 font-medium ${
                        selectedCourseData?.activeTab === 'PREVIEW' 
                          ? 'border-black text-black' 
                          : 'border-transparent text-gray-500'
                      }`}
                      onClick={() => selectedWebsite && selectedCourseData?.connectionState === 'connected' && setWebsites(prev => prev.map(course => 
                        course.id === selectedWebsite ? { ...course, activeTab: 'PREVIEW' } : course
                      ))}
                    >
                      PREVIEW
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 mr-[5%]">
                {selectedCourseData?.activeTab === 'CONNECT' && (
                  <WebsiteConnectTab
                    selectedCourseData={selectedCourseData}
                    websites={websites}
                    setWebsites={setWebsites}
                    assistant={assistant}
                    systemConfig={systemConfig}
                    setCustomModal={setCustomModal}
                    triggerAutoSave={triggerAutoSave}
                    setPendingSave={setPendingSave}
                  />
                )}

                {selectedCourseData?.activeTab === 'PREVIEW' && selectedWebsite && (
                  <WebsitePreviewTab
                    selectedWebsite={selectedWebsite}
                    websites={websites}
                    setWebsites={setWebsites}
                    triggerAutoSave={triggerAutoSave}
                    setPendingSave={setPendingSave}
                    systemConfig={systemConfig}
                    updateExcludedItems={updateExcludedItems}
                    setCustomModal={setCustomModal}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <WebsiteFooter
            websites={websites}
            currentStatus={currentStatus}
            setCurrentStatus={setCurrentStatus}
            pipelineData={pipelineData}
            pendingSave={pendingSave}
            setPendingSave={setPendingSave}
            isWebsiteRNightly={isWebsiteRNightly}
            setIsWebsiteRNightly={setIsWebsiteRNightly}
            isWebsiteFullLoad={isWebsiteFullLoad}
            setIsWebsiteFullLoad={setIsWebsiteFullLoad}
            assistant={assistant}
            systemConfig={systemConfig}
            triggerAutoSave={triggerAutoSave}
            setModalRefreshNModal={setModalRefreshNModal}
            fetchPipelineData={fetchPipelineData}
            setGetDataClickTime={setGetDataClickTime}
            lastSaveTarget={lastSaveTarget}
            onCancelPipeline={handleCancelPipeline}
            cancellingPipeline={cancellingPipeline}
            gettingData={gettingData}
            setGettingData={(value) => {
              const next = typeof value === 'function' ? value(gettingDataRef.current) : value;
              gettingDataRef.current = next;
              setGettingData(next);
            }}
          />
        </div>

        {/* Modals */}
        {showConfirmModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h2 className="text-lg font-semibold">Unsaved Changes</h2>
              <p className="text-gray-600">Your changes have not been saved. Are you sure you want to close?</p>
              <div className="mt-4 flex justify-end space-x-2">
                <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                <button onClick={() => { setWebsiteModalOpen(false); setShowConfirmModal(false); }} className="px-4 py-2 bg-red-500 text-white rounded">Close Anyway</button>
              </div>
            </div>
          </div>
        )}

        <CustomModal
          isOpen={customModal.isOpen}
          onClose={() => setCustomModal({ ...customModal, isOpen: false })}
          onConfirm={customModal.onConfirm}
          title={customModal.title}
          message={customModal.message}
          type={customModal.type}
        />

        <WarningModal
          isOpen={modalRefreshNModal}
          onClose={() => setModalRefreshNModal(false)}
          message="Data will be refreshed nightly only if the assistant is published."
        />
      </div>
    </div>
  );
};

export default PublicWebsites;